import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Check, ChevronRight, MessageSquare, ListChecks, Monitor, Brain, CalendarClock,
  Plug, ShieldCheck, Cpu, Pencil, Code, Megaphone, FileText, Briefcase, Inbox, Video, Rocket,
  ArrowRightLeft, Book, Bot, Users, User, LayoutDashboard, CalendarDays, Smartphone,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import LandingFigure from '../components/LandingFigure';
import NotFoundPage from './NotFoundPage';
import { GUIDE_GROUPS, GUIDES_UPDATED, getGuide } from '../data/guides';
import { DEFAULT_LANGUAGE } from '../i18n/locales';

const ICONS = {
  'message-square': MessageSquare, 'list-checks': ListChecks, monitor: Monitor, brain: Brain,
  'calendar-clock': CalendarClock, plug: Plug, 'shield-check': ShieldCheck, cpu: Cpu, pencil: Pencil,
  code: Code, megaphone: Megaphone, 'file-text': FileText, briefcase: Briefcase, inbox: Inbox,
  video: Video, rocket: Rocket, 'arrow-right-left': ArrowRightLeft, book: Book, bot: Bot, users: Users,
  smartphone: Smartphone,
};

const asList = (v) => (Array.isArray(v) ? v : []);

// Bots work inside the app's own tasks, planner and documents: the bot, freelancer
// and integration pages say so, with the same three pillars as the home page.
const WORK_APP_GROUPS = ['bots', 'usecases', 'apps'];
const PILLARS = [
  { key: 'tasks', path: '/features/projects', Icon: LayoutDashboard },
  { key: 'planner', path: '/features/calendar', Icon: CalendarDays },
  { key: 'docs', path: '/features/documents', Icon: FileText },
];

const WorkAppStrip = ({ t }) => (
  <section className="max-w-7xl mx-auto px-4 py-12">
    <div className="rounded-2xl border border-gray-800 bg-white/[0.03] p-6 sm:p-8">
      <div className="max-w-3xl mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">{t('landing:guide.work_app.title')}</h2>
        <p className="text-gray-400 leading-relaxed">{t('landing:guide.work_app.body')}</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {PILLARS.map(({ key, path, Icon }) => (
          <Link key={key} to={path} className="group p-5 rounded-xl bg-white/5 border border-gray-800 hover:border-amber-500/40 transition-colors">
            <div className="text-white font-semibold mb-1 group-hover:text-amber-400 transition-colors flex items-center gap-2">
              <Icon className="w-4 h-4 text-amber-400" />{t(`landing:home.work_app.items.${key}.t`)}
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">{t(`landing:home.work_app.items.${key}.d`)}</p>
          </Link>
        ))}
      </div>
      <Link to="/#work-app" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-medium mt-6">
        {t('landing:guide.work_app.more')}<ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </section>
);
// The hub's cards fill whole rows: nine bot pages in threes, eight roles in fours, two apps side by side.
const hubColumns = (n) => (n <= 2 ? '' : n % 3 === 0 && n % 4 !== 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-4');

// Link labels for every page live in landing.json under guide_links, keyed by path.
const linkKey = (path) => `guide_links.${path.replace(/^\//, '')}`;

const Eyebrow = ({ guide, label }) => {
  const Icon = ICONS[guide.icon] || Bot;
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 text-amber-400 mb-6 text-xs font-medium">
      {guide.logo
        ? <img src={guide.logo} alt="" className="w-4 h-4 object-contain rounded-sm bg-white p-[1px]" />
        : <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </div>
  );
};

const SectionHead = ({ h, intro }) => (
  <div className="max-w-3xl mb-8">
    {h && <h2 className="text-2xl sm:text-3xl font-bold text-white">{h}</h2>}
    {intro && <p className="text-gray-400 mt-3 leading-relaxed">{intro}</p>}
  </div>
);

/** One content block from the page's JSON. */
const Block = ({ block, t, accent }) => {
  switch (block.type) {
    case 'text':
      return (
        <section className="max-w-3xl mx-auto px-4 py-10">
          <SectionHead h={block.h} />
          <div className="space-y-4">
            {asList(block.p).map((para, i) => (
              <p key={i} className="text-gray-300 text-lg leading-relaxed">{para}</p>
            ))}
          </div>
        </section>
      );
    case 'list':
      return (
        <section className="max-w-3xl mx-auto px-4 py-10">
          <SectionHead h={block.h} intro={block.intro} />
          <ul className="space-y-3">
            {asList(block.items).map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-amber-400" />
                </span>
                <span className="text-gray-300 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </section>
      );
    case 'steps':
      return (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <SectionHead h={block.h} intro={block.intro} />
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {asList(block.items).map((step, i) => (
              <li key={i} className="bg-white/5 border border-gray-800 rounded-2xl p-6">
                <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-400 flex items-center justify-center text-sm font-bold mb-4">{i + 1}</div>
                <h3 className="text-white font-semibold mb-2">{step.t}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{step.d}</p>
              </li>
            ))}
          </ol>
        </section>
      );
    case 'cards':
      return (
        <section className="max-w-7xl mx-auto px-4 py-12">
          <SectionHead h={block.h} intro={block.intro} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {asList(block.items).map((card, i) => (
              <div key={i} className="bg-white/5 border border-gray-800 rounded-2xl p-6">
                <h3 className="text-amber-400 font-semibold mb-2">{card.t}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{card.d}</p>
              </div>
            ))}
          </div>
        </section>
      );
    case 'handoff':
      return (
        <section className="max-w-5xl mx-auto px-4 py-12">
          <SectionHead h={block.h} intro={block.intro} />
          <div className="space-y-4">
            {asList(block.items).map((item, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-px rounded-2xl overflow-hidden border border-gray-800 bg-gray-800">
                <div className="bg-[#111113] p-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">
                    <User className="w-3.5 h-3.5" />{t('landing:guide.you_write')}
                  </div>
                  <p className="text-white leading-relaxed">{item.you}</p>
                </div>
                <div className="bg-[#111113] p-5">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">
                    <Bot className="w-3.5 h-3.5" />{t('landing:guide.bot_does')}
                  </div>
                  <p className="text-gray-300 leading-relaxed">{item.bot}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      );
    case 'table':
      return (
        <section className="max-w-6xl mx-auto px-4 py-12">
          <SectionHead h={block.h} intro={block.intro} />
          <div className="overflow-x-auto rounded-2xl border border-gray-800 bg-white/[0.02]">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-b border-gray-800 bg-white/[0.03]">
                  {asList(block.cols).map((col, i) => (
                    <th key={i} scope="col" className={`px-5 py-4 text-left font-semibold ${accent && i === 1 ? 'text-amber-400' : 'text-gray-300'}`}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {asList(block.rows).map((row, i) => (
                  <tr key={i} className="border-b border-gray-800 last:border-0 align-top">
                    {asList(row).map((cell, j) => (
                      j === 0
                        ? <th key={j} scope="row" className="px-5 py-4 text-left text-gray-300 font-medium">{cell}</th>
                        : <td key={j} className={`px-5 py-4 ${accent && j === 1 ? 'text-amber-200' : j === 1 ? 'text-gray-200' : 'text-gray-400'}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {block.note && <p className="text-xs text-gray-500 mt-4 leading-relaxed">{block.note}</p>}
        </section>
      );
    default:
      return null;
  }
};

const GuidePage = ({ fallback = null }) => {
  const { pathname } = useLocation();
  const guide = getGuide(pathname);
  if (!guide) return fallback || <NotFoundPage />;
  return <Guide guide={guide} />;
};

const Guide = ({ guide }) => {
  const group = GUIDE_GROUPS[guide.group];
  const { t, i18n } = useTranslation([group.ns, 'landing']);
  const c = t(guide.key, { returnObjects: true });
  if (!c || typeof c !== 'object') return <NotFoundPage />;

  const lang = (i18n.resolvedLanguage || DEFAULT_LANGUAGE).split('-')[0];
  const prefix = lang === DEFAULT_LANGUAGE ? '' : `/${lang}`;
  const updated = new Intl.DateTimeFormat(lang, { year: 'numeric', month: 'long', day: 'numeric' })
    .format(new Date(`${guide.updated || GUIDES_UPDATED}T12:00:00Z`));
  const isHub = guide.key === 'index';
  const groupLabel = t(`landing:guide.groups.${guide.group}`);
  const crumbs = [{ name: t('landing:pricing.breadcrumb_home'), url: `${prefix}/` }];
  if (!isHub) crumbs.push({ name: groupLabel, url: `${prefix}${group.hub}` });
  crumbs.push({ name: c.title });
  const faq = asList(c.faq);

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={c.seo_title || c.title}
        description={c.seo_description}
        canonical={guide.path}
        image={guide.image?.replace(/\.webp$/, '.png')}
        faqItems={faq.map((f) => ({ question: f.q, answer: f.a }))}
        breadcrumbs={crumbs}
        article={{
          headline: c.title,
          description: c.seo_description,
          image: guide.image?.replace(/\.webp$/, '.png'),
          datePublished: guide.updated || GUIDES_UPDATED,
          dateModified: guide.updated || GUIDES_UPDATED,
          author: { name: 'BusyBee.Day' },
        }}
      />
      <Navbar />

      <main className="pt-12 pb-16">
        <article>
          <header className="max-w-7xl mx-auto px-4 pt-8 pb-10">
            <nav aria-label={t('landing:guide.breadcrumb')} className="flex flex-wrap items-center gap-2 text-sm text-gray-500 mb-8">
              <Link to="/" className="hover:text-white transition-colors">{crumbs[0].name}</Link>
              {!isHub && (
                <>
                  <ChevronRight className="w-3.5 h-3.5" />
                  <Link to={group.hub} className="hover:text-white transition-colors">{groupLabel}</Link>
                </>
              )}
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-gray-300">{c.title}</span>
            </nav>

            <div className={`grid gap-10 items-center ${guide.image ? 'lg:grid-cols-12' : ''}`}>
              <div className={guide.image ? 'lg:col-span-6' : 'max-w-4xl'}>
                <Eyebrow guide={guide} label={c.eyebrow || groupLabel} />
                <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-5 leading-[1.08]">{c.title}</h1>
                {c.subtitle && <p className="text-xl text-gray-300 mb-8 leading-relaxed">{c.subtitle}</p>}
                <div className="flex flex-wrap gap-3 mb-6">
                  <Link to="/register" className="bg-amber-500 text-black px-6 py-3 rounded-lg font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
                    {t('landing:guide.cta_start')}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/pricing" className="border border-gray-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/5 transition-colors">
                    {t('landing:guide.cta_pricing')}
                  </Link>
                </div>
                <p className="text-sm text-gray-500">
                  <time dateTime={guide.updated || GUIDES_UPDATED}>{t('landing:guide.updated', { date: updated })}</time>
                </p>
              </div>
              {guide.image && (
                <div className="lg:col-span-6">
                  <LandingFigure src={guide.image} alt={c.image_alt || c.title} eager frame="hero" />
                </div>
              )}
            </div>
          </header>

          {c.answer && (
            <section className="max-w-4xl mx-auto px-4 py-8">
              <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 sm:p-8">
                <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-3">{t('landing:guide.short_answer')}</div>
                <p className="text-lg sm:text-xl text-white leading-relaxed">{c.answer}</p>
              </div>
            </section>
          )}

          {isHub && asList(guide.children).length > 0 && (
            <section className="max-w-7xl mx-auto px-4 py-12">
              <div className={`grid sm:grid-cols-2 gap-4 ${hubColumns(guide.children.length)}`}>
                {guide.children.map((path) => {
                  const child = getGuide(path);
                  const Icon = ICONS[child?.icon] || Bot;
                  return (
                    <Link key={path} to={path} className="group p-6 bg-white/5 rounded-2xl border border-gray-800 hover:border-amber-500/40 transition-colors">
                      <Icon className="w-6 h-6 text-amber-400 mb-4" />
                      <h2 className="text-white font-semibold mb-2 group-hover:text-amber-400 transition-colors">{t(`landing:${linkKey(path)}.name`)}</h2>
                      <p className="text-gray-400 text-sm leading-relaxed">{t(`landing:${linkKey(path)}.desc`)}</p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* In comparisons the second column is BusyBee.Day's, so it is highlighted */}
          {asList(c.blocks).map((block, i) => <Block key={i} block={block} t={t} accent={guide.group === 'compare'} />)}

          {faq.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 py-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">{t('landing:guide.faq')}</h2>
              <div className="space-y-3">
                {faq.map((item, i) => (
                  <details key={i} className="group bg-white/5 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-5">
                      <h3 className="text-white font-semibold text-base sm:text-lg">{item.q}</h3>
                      <ChevronRight className="w-5 h-5 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 mt-0.5" />
                    </summary>
                    <div className="px-5 pb-5 text-gray-400 leading-relaxed">{item.a}</div>
                  </details>
                ))}
              </div>
            </section>
          )}
        </article>

        {WORK_APP_GROUPS.includes(guide.group) && <WorkAppStrip t={t} />}

        {asList(guide.related).length > 0 && (
          <nav aria-label={t('landing:guide.related')} className="max-w-7xl mx-auto px-4 py-12">
            <h2 className="text-2xl font-bold text-white mb-6">{t('landing:guide.related')}</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {guide.related.map((path) => (
                <Link key={path} to={path} className="group p-5 bg-white/5 rounded-xl border border-gray-800 hover:border-amber-500/40 transition-colors">
                  <div className="text-white font-semibold mb-1 group-hover:text-amber-400 transition-colors inline-flex items-center gap-1">
                    {t(`landing:${linkKey(path)}.name`)}
                    <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed">{t(`landing:${linkKey(path)}.desc`)}</p>
                </Link>
              ))}
            </div>
          </nav>
        )}

        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('landing:guide.final_title')}</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">{t('landing:guide.final_body')}</p>
          <Link to="/register" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
            {t('landing:guide.cta_start')}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default GuidePage;
